import { all, takeEvery, put, call } from 'redux-saga/effects';
import actions from './actions';
import { sendRequest } from '../../../helpers/saga';
import { logger } from '../../../helpers/logger';
import notifications from '../../../redux/notifications/actions';
import Api from '../../../helpers/api';
import { t } from '../../../i18n';

const agenciesApi = new Api('agencies?pageSize=9999');
const contractsByLegalEntityApi = new Api('legal_entities/:id/agency_contracts');
const legalEntitiesApi = new Api('legal_entities/list');
const updatePreferenceApi = new Api('agencies/:id/update_preference');

function* loadData({ payload }) {
  try {
    const {
      page: _page,
      column: _column,
      order: _order,
      filter: _filter,
      id: _id,
      legalEntityId,
      ...restPayload
    } = payload;

    const result = legalEntityId
      ? yield call(
          sendRequest,
          contractsByLegalEntityApi.load,
          { id: legalEntityId, search: { ...restPayload } },
          notifications.REQUEST_ERROR,
        )
      : yield call(sendRequest, agenciesApi.load, { search: { ...restPayload } }, notifications.REQUEST_ERROR);

    if (result?.data?.success?.data) {
      const { records, filteredCount, filters, sortableColumns, totalCount } = result.data.success.data;

      // Flatten new contract shape (agency nested) to legacy flat shape that
      // cellsUtils expects. Keeps `id` as contract id for row navigation.
      const data = legalEntityId
        ? records.map(record => ({
            id: record.id,
            agency_id: record.agency?.id,
            engine_bound: record.engine_bound,
            preference_level: record.preference_level,
            invite: record.invite,
            legal_entity: record.legal_entity,
            name: record.agency?.name,
            street: record.agency?.street,
            city: record.agency?.city,
            state: record.agency?.state,
            zip: record.agency?.zip,
            contact: record.agency?.contact,
          }))
        : records;

      yield put(
        actions.loadDataSuccess({
          data,
          filteredTotal: filteredCount,
          filters,
          sortableColumns,
          total: totalCount,
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadLegalEntities() {
  try {
    const result = yield call(sendRequest, legalEntitiesApi.load, {});
    const records = result?.data?.success?.data?.records;

    if (records) {
      yield put(actions.loadLegalEntitiesSuccess(records));
    } else {
      yield put(actions.loadLegalEntitiesError());
    }
  } catch (error) {
    // Endpoint returns 403 when legal_entity_enabled flag is OFF — fall back to legacy UI silently.
    logger.warn(error);
    yield put(actions.loadLegalEntitiesError());
  }
}

function* updateAgencyPreference({ payload }) {
  try {
    const { id, preference_level, cb } = payload;

    const result = yield call(
      sendRequest,
      updatePreferenceApi.updateItem,
      {
        id,
        data: { agency: { preference_level } },
      },
      notifications.REQUEST_ERROR,
    );

    if (result?.data) {
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.updated_successfully', {
            item: t('dash.agencies.active.priority_label'),
          }),
        }),
      );

      if (cb) {
        cb();
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(actions.LOAD_LEGAL_ENTITIES_REQUEST, loadLegalEntities),
    takeEvery(actions.LOAD_REQUEST, loadData),
    takeEvery(actions.UPDATE_PREFERENCE_REQUEST, updateAgencyPreference),
  ]);
}
